import { wrapCreateBrowserRouterV6 } from '@sentry/react';
import { jwtDecode } from 'jwt-decode';
import React from 'react';
import { LoaderFunctionArgs, Navigate, RouteObject, createBrowserRouter } from 'react-router-dom';

import { ModalLayout, Widget } from '../../components/layouts';
import { FullPageDemo, ModalDemo, ProfileDemo } from '../../components/screens/demo';
import { RouteError } from '../../components/screens/error';
import { BookingFlow, BookingTabs, CustomerBookingFlow, SearchResultsScreen } from '../../components/screens/insert';
import { customerService } from '../../services/customer';
import { siteService } from '../../services/site';
import { userService } from '../../services/user';
import { LoadingScreen } from 'src/components/screens/LoadingScreen';
import { AutomedReferrals } from 'src/components/screens/automed/AutomedReferrals';
import { AutomedScripts } from 'src/components/screens/automed/AutomedScripts';

const demoRoutes: RouteObject[] = [
    {
        path: 'demo/',
        children: [
            {
                path: 'doctor-modal/',
                element: <ModalDemo modalPath="/widget/doctor/16658/modal" />,
            },
            {
                path: 'doctor-profile/',
                element: <ProfileDemo iframePath="/widget/doctor/15079/widget" />,
            },
            {
                path: 'clinic-modal/',
                children: [
                    {
                        index: true,
                        element: <ModalDemo modalPath="/widget/clinic/2032/modal?afterHours=True" />,
                    },
                    {
                        path: 'express/',
                        element: <ModalDemo modalPath="/widget/clinic/2032/modal?serviceType=express%20telehealth" />,
                    },
                ],
            },
            {
                path: 'clinic-profile/',
                element: <ProfileDemo iframePath="/widget/clinic/2032/widget?afterHours=True" />,
            },
            {
                path: 'search-results',
                element: <FullPageDemo iframePath="/widget/search/results?state=NSW&postcode=2000&suburb=Sydney" />,
            },
        ],
    },
];

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV6(createBrowserRouter);

export const router = sentryCreateBrowserRouter([
    {
        path: '/widget',
        ErrorBoundary: () => <RouteError />,
        hydrateFallbackElement: <LoadingScreen />,
        children: [
            import.meta.env.MODE === 'development'
                ? {
                      index: true,
                      element: <Navigate to="/widget/demo/clinic-modal" />,
                  }
                : {
                      index: true,
                      loader: () => {
                          throw new Response('Not Found', { status: 404 });
                      },
                  },
            {
                path: 'doctor/:doctorId/',
                children: [
                    {
                        loader: async ({ params }: LoaderFunctionArgs) => {
                            return { doctor: await userService.GetSitesByUmbracoUser(params.doctorId!) };
                        },
                        path: 'modal/',
                        element: (
                            <ModalLayout>
                                <BookingFlow isModal />
                            </ModalLayout>
                        ),
                    },
                    {
                        loader: async ({ params }: LoaderFunctionArgs) => {
                            return { doctor: await userService.GetSitesByUmbracoUser(params.doctorId!) };
                        },
                        path: 'widget/',
                        element: (
                            <Widget>
                                <BookingFlow />
                            </Widget>
                        ),
                    },
                ],
            },
            {
                path: 'clinic/:clinicId/',
                children: [
                    {
                        loader: async ({ params }: LoaderFunctionArgs) => {
                            return { site: await siteService.GetSiteByUmbracoId(params.clinicId!) };
                        },
                        path: 'modal/',
                        element: (
                            <ModalLayout>
                                <BookingTabs>
                                    <BookingFlow isClinicPage isModal className="rounded-tl-none" />
                                </BookingTabs>
                            </ModalLayout>
                        ),
                    },
                    {
                        loader: async ({ params }: LoaderFunctionArgs) => {
                            return { site: await siteService.GetSiteByUmbracoId(params.clinicId!) };
                        },
                        path: 'widget/',
                        element: (
                            <Widget>
                                <BookingTabs>
                                    <BookingFlow isClinicPage className="rounded-tl-none" />
                                </BookingTabs>
                            </Widget>
                        ),
                    },
                ],
            },
            {
                path: 'search/',
                children: [
                    {
                        path: 'telehealth',
                    },
                    {
                        path: 'results',
                        element: (
                            <Widget>
                                <SearchResultsScreen />
                            </Widget>
                        ),
                    },
                ],
            },
            {
                path: 'customer',
                children: [
                    {
                        loader: async ({ params, request: { url } }: LoaderFunctionArgs) => {
                            const customerId = getCustomerIdFromSearchParams(url);
                            return {
                                doctor: await userService.GetSitesByUmbracoUser(params.doctorId!),
                                customer: await customerService.GetById(customerId),
                            };
                        },
                        path: 'doctor/:doctorId/',
                        element: <CustomerBookingFlow />,
                    },
                    {
                        loader: async ({ params, request: { url } }: LoaderFunctionArgs) => {
                            const customerId = getCustomerIdFromSearchParams(url);

                            return {
                                site: await siteService.GetSiteByUmbracoId(params.clinicId!),
                                customer: await customerService.GetById(customerId),
                            };
                        },
                        path: 'clinic/:clinicId/',
                        element: <CustomerBookingFlow isClinicPage />,
                    },
                ],
            },
            {
                path: 'repeats/scripts/:automedSiteId/:automedPath/:locationId',
                element: <AutomedScripts />,
            },
            {
                path: 'repeats/referrals/:automedSiteId/:automedPath/:locationId',
                element: <AutomedReferrals />,
            },
            ...(import.meta.env.MODE === 'development' ? demoRoutes : []),
        ],
    },
]);

function getCustomerIdFromSearchParams(url: string): number {
    const jwt = new URL(url).searchParams.get('token');
    if (!jwt) {
        throw 'Invalid token';
    }
    if (!customerService.Token) {
        customerService.Token = jwt;
    }
    const decode = jwtDecode<{ customer_id: string }>(jwt);
    const customerId = Number(decode.customer_id);
    if (!customerId || isNaN(customerId)) {
        throw 'Invalid customer ID';
    }
    return customerId;
}
